import { Button, Col, Divider, Modal, Row } from "antd";
import React from "react";
import { resolveSubMeta } from "../../../helper/masterSuiteFlow";
import { changeSubCategoryValue } from "../../../helper/string_helper";

function LabelValue({ label, value }) {
  if (!value) return null;
  return (
    <Row style={{ marginBottom: 6 }}>
      <Col span={8}>
        <p
          className="spec-modal-label"
          style={{ color: "#888", marginBottom: 0 }}
        >
          {label}
        </p>
      </Col>
      <Col span={16}>
        <p
          className="spec-modal-label"
          style={{ fontWeight: 500, marginBottom: 0 }}
        >
          {value}
        </p>
      </Col>
    </Row>
  );
}

function Section({ label, children }) {
  return (
    <>
      <Row style={{ margin: "12px 0 6px" }}>
        <Col span={24}>
          <Divider
            style={{
              fontSize: 13,
              color: "#0B1F3A",
              fontWeight: 500,
              margin: "8px 0",
            }}
          >
            {label}
          </Divider>
        </Col>
      </Row>
      {children}
    </>
  );
}

function FileSection({
  label,
  record,
  locationKey = "inputLocationOne",
  envKey = "environmentOne",
  fromKey = "inputFromOne",
}) {
  const location = record[locationKey];
  const env = record[envKey];
  const from = record[fromKey];
  if (!location && !from) return null;
  return (
    <Section label={label}>
      <LabelValue label="Input Location" value={location} />
      {location === "Environment location" && (
        <LabelValue label="Environment" value={env} />
      )}
      <LabelValue label="Input from" value={from} />
    </Section>
  );
}

function DbSection({ label, record, envKey, dbKey }) {
  const env = record[envKey];
  const db = record[dbKey];
  if (!env && !db) return null;
  return (
    <Section label={label}>
      <LabelValue label="Environment" value={env} />
      <LabelValue label="Database" value={db} />
    </Section>
  );
}

// ─── Per sub-category view renderers ─────────────────────────
const VIEWS = {
  "File to Specification": ({ record }) => (
    <FileSection label="File Parameters" record={record} />
  ),

  "File to Database": ({ record }) => (
    <>
      <FileSection label="File Parameters" record={record} />
      <DbSection
        label="Database Parameters"
        record={record}
        envKey="environmentTwo"
        dbKey="databaseOne"
      />
    </>
  ),

  "Database to Database": ({ record }) => (
    <>
      <DbSection
        label="Source Database"
        record={record}
        envKey="environmentOne"
        dbKey="databaseOne"
      />
      <DbSection
        label="Target Database"
        record={record}
        envKey="environmentTwo"
        dbKey="databaseTwo"
      />
    </>
  ),

  "File to File": ({ record }) => (
    <>
      <FileSection
        label="File One Parameters"
        record={record}
        locationKey="inputLocationOne"
        envKey="environmentOne"
        fromKey="inputFromOne"
      />
      <FileSection
        label="File Two Parameters"
        record={record}
        locationKey="inputLocationTwo"
        envKey="environmentTwo"
        fromKey="inputFromTwo"
      />
    </>
  ),

  "File Move, Copy or Delete": ({ record }) => (
    <>
      <FileSection
        label="Source Location"
        record={record}
        locationKey="inputLocationOne"
        envKey="environmentOne"
        fromKey="inputFromOne"
      />
      <FileSection
        label="Destination Location"
        record={record}
        locationKey="inputLocationTwo"
        envKey="environmentTwo"
        fromKey="inputFromTwo"
      />
    </>
  ),

  "Extract Compressed File": ({ record }) => (
    <FileSection label="File Location" record={record} />
  ),

  "Event Monitor": ({ record }) => {
    const isSql = (record.eventType || "").toLowerCase().includes("sql");
    return (
      <>
        <LabelValue label="Event Type" value={record.eventType || "—"} />
        {isSql ? (
          <DbSection
            label="Database Connection"
            record={record}
            envKey="environmentTwo"
            dbKey="databaseOne"
          />
        ) : (
          <FileSection label="File Location" record={record} />
        )}
      </>
    );
  },
};

// Coloured sub-category pill — matches the workflow node colours.
function SubCategoryPill({ category, rawSub, label }) {
  const meta = resolveSubMeta(category, rawSub);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 12px",
        background: meta.tint,
        color: meta.color,
        border: `1px solid ${meta.color}`,
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}

// Step + task meta shown above the parameters when a full step row is supplied.
function StepHeader({ step, subCatLabel }) {
  const typeLabel = step.type === "masterSuite" ? "Master Suite" : "Task";
  return (
    <>
      <LabelValue
        label="Step"
        value={
          step.stepReference
            ? step.sequence != null
              ? `${step.stepReference}  (#${step.sequence})`
              : step.stepReference
            : null
        }
      />
      <LabelValue label="Type" value={typeLabel} />
      <LabelValue label="Task / Suite" value={step.taskName} />
      <Row style={{ marginBottom: 6 }}>
        <Col span={8}>
          <p className="spec-modal-label" style={{ color: "#888", marginBottom: 0 }}>
            Sub Category
          </p>
        </Col>
        <Col span={16}>
          <SubCategoryPill
            category={step.categoryValue || step.category}
            rawSub={step.subCategoryValue || step.subcategory}
            label={subCatLabel}
          />
        </Col>
      </Row>
      <LabelValue
        label="Stop if Failed"
        value={step.stopIfFailed ? "On" : "Off"}
      />
    </>
  );
}

// ─── Main view modal ──────────────────────────────────────────
function VariablesViewModal({ open, record = {}, step, handleVariableModal }) {
  // Resolve the sub-category to its label (VIEWS is keyed by labels). Builder
  // rows store the key (file2spec); view rows store the label — changeSubCategoryValue
  // maps a key to its label and returns labels unchanged.
  const rawSub = step
    ? step.subCategoryValue || step.subcategory || ""
    : record.subCategoryValue || record.subCategory || "";
  const subCat = changeSubCategoryValue(rawSub);
  const ViewComp = VIEWS[subCat];
  const params = step ? step.variables || {} : record;

  return (
    <Modal
      open={open}
      width="48%"
      onCancel={handleVariableModal}
      footer={[
        <Button key="ok" type="primary" onClick={handleVariableModal}>
          Close
        </Button>,
      ]}
    >
      <div style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: 4 }}>
        <Row style={{ paddingBottom: 16 }}>
          <p className="modal-header-text">
            {step ? "Step Details" : "View Parameters"}
          </p>
        </Row>

        {step ? (
          <StepHeader step={step} subCatLabel={subCat} />
        ) : (
          /* Sub-category badge (legacy "Parameters → View" usage) */
          <Row style={{ marginBottom: 12 }}>
            <Col span={8}>
              <p className="spec-modal-label" style={{ color: "#888" }}>
                Sub Category
              </p>
            </Col>
            <Col span={16}>
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 12px",
                  background: "#E6F1FB",
                  color: "#185FA5",
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {subCat}
              </span>
            </Col>
          </Row>
        )}

        {step && <Divider style={{ margin: "8px 0 4px" }} />}

        {ViewComp ? (
          <ViewComp record={params} />
        ) : (
          <div
            style={{ color: "#888", padding: "20px 0", textAlign: "center" }}
          >
            No parameters configured for this step type.
          </div>
        )}
      </div>
    </Modal>
  );
}

export default VariablesViewModal;
